// The one implementation of "a set of links to other pages, marking which
// one you're on" (`role="navigation"` + `aria-current="page"` on the active
// item - real route changes, not an in-place content swap, so this is a
// `<nav>` list, not a tablist). Previously PjShell (the project shell's own
// rail) and the Docs sidebar each rendered `.pj-rail`/`.pj-rail-link`
// directly; the Legal page's Terms/Privacy/Acceptable-use switcher did the
// same horizontally via `.range-chip`, even though it's the identical kind
// of control (a link list marking the current page) just laid out
// sideways. Account settings' visually-identical vertical switcher
// hand-rolled a third, independent copy of the same markup even though it
// isn't route-driven at all (see engine/editor/components/Tabs.tsx's 'rail'
// variant for that one - a tablist that happens to be laid out as a
// sidebar).
//
// Lives in src/studio/, not the engine layer: every real consumer is
// studio-only and needs react-router-dom's <Link>, which the engine layer
// has no reason to depend on.
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Link } from '../design/router';

export interface SectionNavProps {
  label: string;
  children: ReactNode;
  /** 'vertical' (default) matches the project shell/Docs sidebar rail.
   * 'horizontal' matches the Legal page's row of page links - same
   * `.range-chip` pill language TabStrip's 'compact' variant uses, since
   * both are "a row of small pill-shaped switchers", just one navigates
   * and one doesn't. */
  orientation?: 'vertical' | 'horizontal';
  className?: string;
  style?: CSSProperties;
}

export function SectionNav({ label, children, orientation = 'vertical', className, style }: SectionNavProps) {
  const base = orientation === 'horizontal' ? 'ss-tabstrip--compact' : 'pj-rail';
  return (
    <nav aria-label={label} className={base + (className ? ` ${className}` : '')} style={style}>
      {children}
    </nav>
  );
}

export interface SectionNavItemProps {
  to: string;
  active: boolean;
  children: ReactNode;
  orientation?: 'vertical' | 'horizontal';
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
  style?: CSSProperties;
}

export function SectionNavItem({ to, active, children, orientation = 'vertical', onClick, className, style }: SectionNavItemProps) {
  const base = orientation === 'horizontal' ? 'range-chip' : 'pj-rail-link';
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={base + (active ? ' is-active' : '') + (className ? ` ${className}` : '')}
      style={style}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

/** A non-interactive placeholder for a nav entry with nothing to link to yet
 * (the Docs sidebar's "coming soon" rows) - same visual slot, no `<Link>`,
 * no `aria-current`, so it can't be mistaken for a reachable page. Takes the
 * same `orientation` as `SectionNavItem` so it stays a same-visual-slot
 * placeholder in a horizontal `SectionNav` too, not just the vertical rail. */
export function SectionNavPlaceholder({ children, orientation = 'vertical', style }: { children: ReactNode; orientation?: 'vertical' | 'horizontal'; style?: CSSProperties }) {
  const base = orientation === 'horizontal' ? 'range-chip' : 'pj-rail-link';
  return (
    <span className={base} style={{ opacity: 0.45, cursor: 'default', ...style }}>
      {children}
    </span>
  );
}
